import { TFunction } from 'i18next';
import { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { GenericResponse } from 'src/js/model/api/response/generic-response';
import { isNilOrEmpty } from 'src/js/util/global-util';
import * as yup from 'yup';
import { MkRange } from '../video-encoder/video-encoder-models';

export enum AudioEncoderState {
  STOPPED = 0,
  WORKING = (1 << 0) | (1 << 1),
  MUTED = (1 << 0) | (1 << 1) | (1 << 6),
  FAILED = 1 << 7,
}

export enum AudioAlgorithmEnum {
  PCM = 0,
  RAW, // 16-bit passthrough
  MPEG2_AAC_LC_ADTS = 10,
  MPEG4_AAC_LC_ADTS = 20,
  MPEG4_AAC_LC_LOAS,
  MPEG4_AAC_HE_V1_LOAS,
  MPEG4_AAC_HE_V2_LOAS,
}

export enum AudioInterfaceEnum {
  ANALOG = 0,
  SDI1_CH12 /* 1 */,
  SDI1_CH34,
  SDI1_CH56,
  SDI1_CH78,
  SDI1_CH9_10,
  SDI1_CH11_12,
  SDI1_CH13_14,
  SDI1_CH15_16,
  SDI2_CH12 /* 9 */,
  SDI2_CH34,
  SDI2_CH56,
  SDI2_CH78,
  SDI2_CH9_10,
  SDI2_CH11_12,
  SDI2_CH13_14,
  SDI2_CH15_16,
  SDI3_CH12 /* 17 */,
  SDI3_CH34,
  SDI3_CH56,
  SDI3_CH78,
  SDI3_CH9_10,
  SDI3_CH11_12,
  SDI3_CH13_14,
  SDI3_CH15_16,
  SDI4_CH12 /* 25 */,
  SDI4_CH34,
  SDI4_CH56,
  SDI4_CH78,
  SDI4_CH9_10,
  SDI4_CH11_12,
  SDI4_CH13_14,
  SDI4_CH15_16,
  HDMI_CH12 /* 33 */,
}

export enum AudioEncoderMode {
  STEREO = 0,
  LEFT,
  RIGHT,
  SURROUND_THREE,
  SURROUND_FOUR,
  SURROUND_FIVE,
  SURROUND_SIX /* 5.1 */,
  SURROUND_SEVEN,
  SURROUND_EIGHT,
}

export const audioLanguages: { [key: string]: string } = {
  sqi: 'Albanian',
  ara: 'Arabic',
  hye: 'Armenian',
  arm: 'Armenian',
  bul: 'Bulgarian',
  zho: 'Chinese',
  chi: 'Chinese',
  hrv: 'Croatian',
  ces: 'Czech',
  cze: 'Czech',
  dan: 'Danish',
  nld: 'Dutch',
  dut: 'Dutch',
  eng: 'English',
  est: 'Estonian',
  fin: 'Finnish',
  fre: 'French',
  fra: 'French',
  deu: 'German',
  ger: 'German',
  ell: 'Greek',
  gre: 'Greek',
  heb: 'Hebrew',
  hin: 'Hindi',
  hun: 'Hungarian',
  ind: 'Indonesian',
  gle: 'Irish',
  isl: 'Icelandic',
  ice: 'Icelandic',
  ita: 'Italian',
  jpn: 'Japanese',
  khm: 'Khmer',
  kor: 'Korean',
  lav: 'Latvian',
  lit: 'Lithuanian',
  msa: 'Malay',
  may: 'Malay',
  mlt: 'Maltese',
  mon: 'Mongolian',
  nor: 'Norwegian',
  pan: 'Punjabi',
  fas: 'Persian',
  per: 'Persian',
  pol: 'Polish',
  por: 'Portugese',
  ron: 'Romanian',
  rum: 'Romanian',
  rus: 'Russian',
  slk: 'Slovak',
  slo: 'Slovak',
  slv: 'Slovenian',
  spa: 'Spanish',
  swa: 'Swahili',
  swe: 'Swedish',
  tur: 'Turkish',
  ukr: 'Ukrainian',
  vie: 'Vietnamese',
  tlh: 'Klingon',
};

export interface AudioEncoder {
  algorithm: AudioAlgorithmEnum;
  bitRate: number | string; // needs to be string for Typeahead to behave properly, in AudioEncoderViewModel it's mapped to string
  id: number;
  inputLevel: number;
  interface: AudioInterfaceEnum;
  lang: string;
  mode: number;
  name: string;
  sampleRate: number;
}

export interface AudioEncoderStats {
  encodedFrames: string;
  encodedBytes: string;
  encodedBitrate: string;
  encoderErrors: string;
  encoderPTS: string;
  lastReset: string;
  maxSampleValue?: number;
  maxSampleValuePercentage?: number;
  maximumVuLevel?: string;
  encodedFramerate?: string;
  passthroughType?: string;
  passthroughDetectedAlgorithm?: string;
  passthroughDetectedSamplerate?: string;
  passthroughDetectedBitDepth?: string;
  passthroughErrors?: string;
  state: AudioEncoderState;
}

export const encoderIsMuted = (state: AudioEncoderState): boolean => state === AudioEncoderState.MUTED;
export const encoderIsStopped = (state: AudioEncoderState): boolean =>
  state === AudioEncoderState.STOPPED || state === AudioEncoderState.FAILED;
export const encoderIsStarted = (state: AudioEncoderState): boolean =>
  state === AudioEncoderState.WORKING || state === AudioEncoderState.FAILED;

export const encoderStateToIcon = (state: AudioEncoderState): string => {
  switch (state) {
    case AudioEncoderState.STOPPED:
      return 'StatusStopped';
    case AudioEncoderState.WORKING:
      return 'StatusOK';
    case AudioEncoderState.MUTED:
      return 'StatusAudioOff';
    default:
      return 'StatusError';
  }
};

export const encoderStateToName = (state: AudioEncoderState, t: TFunction): string => {
  switch (state) {
    case AudioEncoderState.STOPPED:
      return t('audioEncoder.states.stopped');
    case AudioEncoderState.WORKING:
      return t('audioEncoder.states.active');
    case AudioEncoderState.MUTED:
      return t('audioEncoder.states.muted');
    case AudioEncoderState.FAILED:
      return t('audioEncoder.states.failed');
  }
  return '';
};

const encoderStateToSortable = (state: AudioEncoderState): number => {
  switch (state) {
    case AudioEncoderState.WORKING:
      return 1;
    case AudioEncoderState.MUTED:
      return 2;
    case AudioEncoderState.STOPPED:
      return 3;
    case AudioEncoderState.FAILED:
      return 4;
    default:
      return 0;
  }
};

export const encoderStateToColor = (state: AudioEncoderState) => {
  switch (state) {
    case AudioEncoderState.STOPPED:
      return 'haiui-gray-07';
    case AudioEncoderState.MUTED:
      return 'haiui-amber-01';
    case AudioEncoderState.WORKING:
      return 'haiui-green-01';
    default:
      return undefined;
  }
};

// ActionBar status types
export const encoderStateToStatus = (state: AudioEncoderState): string => {
  switch (state) {
    case AudioEncoderState.STOPPED:
      return 'INACTIVE';
    case AudioEncoderState.WORKING:
      return 'ENCODING';
    case AudioEncoderState.MUTED:
      return 'MUTED';
  }
  return '';
};
export const encoderModelToStatus = (model: AudioEncoderViewModel): string => {
  return encoderStateToStatus(model.state);
};

export const getAudioInterfaceLabels = (audioInterface: AudioInterfaceEnum, layout: AudioEncoderMode) => {
  const audioEncoderInterface: {
    interfaceStr: string;
    inputInterface: string;
    inputChannel: string;
    value: AudioInterfaceEnum;
  } = {
    interfaceStr: '',
    inputInterface: '',
    inputChannel: '',
    value: audioInterface,
  };

  let channelSpan = 2;
  if (layout === AudioEncoderMode.SURROUND_THREE) {
    channelSpan = 4;
  } else if (layout === AudioEncoderMode.SURROUND_FOUR) {
    channelSpan = 4;
  } else if (layout === AudioEncoderMode.SURROUND_FIVE) {
    channelSpan = 6;
  } else if (layout === AudioEncoderMode.SURROUND_SIX) {
    channelSpan = 6;
  } else if (layout === AudioEncoderMode.SURROUND_SEVEN) {
    channelSpan = 8;
  } else if (layout === AudioEncoderMode.SURROUND_EIGHT) {
    channelSpan = 8;
  }

  switch (audioInterface) {
    case AudioInterfaceEnum.ANALOG:
      audioEncoderInterface.interfaceStr = 'Analog';
      audioEncoderInterface.inputChannel = 'Ch. 1-2';
      audioEncoderInterface.inputInterface = 'Analog';
      return audioEncoderInterface;
    case AudioInterfaceEnum.SDI1_CH12:
      audioEncoderInterface.interfaceStr = '1 Ch. 1-2';
      audioEncoderInterface.inputChannel = 'Ch. 1-2';
      audioEncoderInterface.inputInterface = 'SDI-1';
      break;
    case AudioInterfaceEnum.SDI1_CH34:
      audioEncoderInterface.interfaceStr = '1 Ch. 3-4';
      audioEncoderInterface.inputChannel = 'Ch. 3-4';
      audioEncoderInterface.inputInterface = 'SDI-1';
      break;
    case AudioInterfaceEnum.SDI1_CH56:
      audioEncoderInterface.interfaceStr = '1 Ch. 5-6';
      audioEncoderInterface.inputChannel = 'Ch. 5-6';
      audioEncoderInterface.inputInterface = 'SDI-1';
      break;
    case AudioInterfaceEnum.SDI1_CH78:
      audioEncoderInterface.interfaceStr = '1 Ch. 7-8';
      audioEncoderInterface.inputChannel = 'Ch. 7-8';
      audioEncoderInterface.inputInterface = 'SDI-1';
      break;
    case AudioInterfaceEnum.SDI1_CH9_10:
      audioEncoderInterface.interfaceStr = '1 Ch. 9-10';
      audioEncoderInterface.inputChannel = 'Ch. 9-10';
      audioEncoderInterface.inputInterface = 'SDI-1';
      break;
    case AudioInterfaceEnum.SDI1_CH11_12:
      audioEncoderInterface.interfaceStr = '1 Ch. 11-12';
      audioEncoderInterface.inputChannel = 'Ch. 11-12';
      audioEncoderInterface.inputInterface = 'SDI-1';
      break;
    case AudioInterfaceEnum.SDI1_CH13_14:
      audioEncoderInterface.interfaceStr = '1 Ch. 13-14';
      audioEncoderInterface.inputChannel = 'Ch. 13-14';
      audioEncoderInterface.inputInterface = 'SDI-1';
      break;
    case AudioInterfaceEnum.SDI1_CH15_16:
      audioEncoderInterface.interfaceStr = '1 Ch. 15-16';
      audioEncoderInterface.inputChannel = 'Ch. 15-16';
      audioEncoderInterface.inputInterface = 'SDI-1';
      break;
    case AudioInterfaceEnum.SDI2_CH12: // SDI-2
      audioEncoderInterface.interfaceStr = '2 Ch. 1-2';
      audioEncoderInterface.inputChannel = 'Ch. 1-2';
      audioEncoderInterface.inputInterface = 'SDI-2';
      break;
    case AudioInterfaceEnum.SDI2_CH34:
      audioEncoderInterface.interfaceStr = '2 Ch. 3-4';
      audioEncoderInterface.inputChannel = 'Ch. 3-4';
      audioEncoderInterface.inputInterface = 'SDI-2';
      break;
    case AudioInterfaceEnum.SDI2_CH56:
      audioEncoderInterface.interfaceStr = '2 Ch. 5-6';
      audioEncoderInterface.inputChannel = 'Ch. 5-6';
      audioEncoderInterface.inputInterface = 'SDI-2';
      break;
    case AudioInterfaceEnum.SDI2_CH78:
      audioEncoderInterface.interfaceStr = '2 Ch. 7-8';
      audioEncoderInterface.inputChannel = 'Ch. 7-8';
      audioEncoderInterface.inputInterface = 'SDI-2';
      break;
    case AudioInterfaceEnum.SDI2_CH9_10:
      audioEncoderInterface.interfaceStr = '2 Ch. 9-10';
      audioEncoderInterface.inputChannel = 'Ch. 9-10';
      audioEncoderInterface.inputInterface = 'SDI-2';
      break;
    case AudioInterfaceEnum.SDI2_CH11_12:
      audioEncoderInterface.interfaceStr = '2 Ch. 11-12';
      audioEncoderInterface.inputChannel = 'Ch. 11-12';
      audioEncoderInterface.inputInterface = 'SDI-2';
      break;
    case AudioInterfaceEnum.SDI2_CH13_14:
      audioEncoderInterface.interfaceStr = '2 Ch. 13-14';
      audioEncoderInterface.inputChannel = 'Ch. 13-14';
      audioEncoderInterface.inputInterface = 'SDI-2';
      break;
    case AudioInterfaceEnum.SDI2_CH15_16:
      audioEncoderInterface.interfaceStr = '2 Ch. 15-16';
      audioEncoderInterface.inputChannel = 'Ch. 15-16';
      audioEncoderInterface.inputInterface = 'SDI-2';
      break;
    case AudioInterfaceEnum.SDI3_CH12: // SDI-3
      audioEncoderInterface.interfaceStr = '3 Ch. 1-2';
      audioEncoderInterface.inputChannel = 'Ch. 1-2';
      audioEncoderInterface.inputInterface = 'SDI-3';
      break;
    case AudioInterfaceEnum.SDI3_CH34:
      audioEncoderInterface.interfaceStr = '3 Ch. 3-4';
      audioEncoderInterface.inputChannel = 'Ch. 3-4';
      audioEncoderInterface.inputInterface = 'SDI-3';
      break;
    case AudioInterfaceEnum.SDI3_CH56:
      audioEncoderInterface.interfaceStr = '3 Ch. 5-6';
      audioEncoderInterface.inputChannel = 'Ch. 5-6';
      audioEncoderInterface.inputInterface = 'SDI-3';
      break;
    case AudioInterfaceEnum.SDI3_CH78:
      audioEncoderInterface.interfaceStr = '3 Ch. 7-8';
      audioEncoderInterface.inputChannel = 'Ch. 7-8';
      audioEncoderInterface.inputInterface = 'SDI-3';
      break;
    case AudioInterfaceEnum.SDI3_CH9_10:
      audioEncoderInterface.interfaceStr = '3 Ch. 9-10';
      audioEncoderInterface.inputChannel = 'Ch. 9-10';
      audioEncoderInterface.inputInterface = 'SDI-3';
      break;
    case AudioInterfaceEnum.SDI3_CH11_12:
      audioEncoderInterface.interfaceStr = '3 Ch. 11-12';
      audioEncoderInterface.inputChannel = 'Ch. 11-12';
      audioEncoderInterface.inputInterface = 'SDI-3';
      break;
    case AudioInterfaceEnum.SDI3_CH13_14:
      audioEncoderInterface.interfaceStr = '3 Ch. 13-14';
      audioEncoderInterface.inputChannel = 'Ch. 13-14';
      audioEncoderInterface.inputInterface = 'SDI-3';
      break;
    case AudioInterfaceEnum.SDI3_CH15_16:
      audioEncoderInterface.interfaceStr = '3 Ch. 15-16';
      audioEncoderInterface.inputChannel = 'Ch. 15-16';
      audioEncoderInterface.inputInterface = 'SDI-3';
      break;
    case AudioInterfaceEnum.SDI4_CH12: // SDI-4
      audioEncoderInterface.interfaceStr = '4 Ch. 1-2';
      audioEncoderInterface.inputChannel = 'Ch. 1-2';
      audioEncoderInterface.inputInterface = 'SDI-4';
      break;
    case AudioInterfaceEnum.SDI4_CH34:
      audioEncoderInterface.interfaceStr = '4 Ch. 3-4';
      audioEncoderInterface.inputChannel = 'Ch. 3-4';
      audioEncoderInterface.inputInterface = 'SDI-4';
      break;
    case AudioInterfaceEnum.SDI4_CH56:
      audioEncoderInterface.interfaceStr = '4 Ch. 5-6';
      audioEncoderInterface.inputChannel = 'Ch. 5-6';
      audioEncoderInterface.inputInterface = 'SDI-4';
      break;
    case AudioInterfaceEnum.SDI4_CH78:
      audioEncoderInterface.interfaceStr = '4 Ch. 7-8';
      audioEncoderInterface.inputChannel = 'Ch. 7-8';
      audioEncoderInterface.inputInterface = 'SDI-4';
      break;
    case AudioInterfaceEnum.SDI4_CH9_10:
      audioEncoderInterface.interfaceStr = '4 Ch. 9-10';
      audioEncoderInterface.inputChannel = 'Ch. 9-10';
      audioEncoderInterface.inputInterface = 'SDI-4';
      break;
    case AudioInterfaceEnum.SDI4_CH11_12:
      audioEncoderInterface.interfaceStr = '4 Ch. 11-12';
      audioEncoderInterface.inputChannel = 'Ch. 11-12';
      audioEncoderInterface.inputInterface = 'SDI-4';
      break;
    case AudioInterfaceEnum.SDI4_CH13_14:
      audioEncoderInterface.interfaceStr = '4 Ch. 13-14';
      audioEncoderInterface.inputChannel = 'Ch. 13-14';
      audioEncoderInterface.inputInterface = 'SDI-4';
      break;
    case AudioInterfaceEnum.SDI4_CH15_16:
      audioEncoderInterface.interfaceStr = '4 Ch. 15-16';
      audioEncoderInterface.inputChannel = 'Ch. 15-16';
      audioEncoderInterface.inputInterface = 'SDI-4';
      break;
    case AudioInterfaceEnum.HDMI_CH12:
      audioEncoderInterface.interfaceStr = 'HDMI Ch. 1-2';
      audioEncoderInterface.inputChannel = 'Ch. 1-2';
      audioEncoderInterface.inputInterface = 'HDMI';
      return audioEncoderInterface;
  }
  if (systemInfoCapabilities.is2110()) {
    // 2110
    audioEncoderInterface.inputInterface = audioEncoderInterface.inputInterface.replace(/^SDI-/, '');
  } else {
    audioEncoderInterface.interfaceStr = 'SDI-' + audioEncoderInterface.interfaceStr;
  }

  if (channelSpan >= 3) {
    let secondDigitPos = audioEncoderInterface.inputChannel.lastIndexOf('-');
    const secondDigit = Number(audioEncoderInterface.inputChannel.substring(secondDigitPos + 1));
    audioEncoderInterface.inputChannel = audioEncoderInterface.inputChannel.substring(0, secondDigitPos);
    const endChannel = secondDigit + channelSpan - 2; // second digit is always 1 up what we want target, and the first digit is counted, so -2 gives the right end value
    if (endChannel > 16) {
      return undefined;
    }
    audioEncoderInterface.inputChannel += `-${endChannel}`;

    secondDigitPos = audioEncoderInterface.interfaceStr.lastIndexOf('-');
    audioEncoderInterface.interfaceStr = audioEncoderInterface.interfaceStr.substring(0, secondDigitPos);
    audioEncoderInterface.interfaceStr += `-${endChannel}`;
  }

  return audioEncoderInterface;
};

export const encoderMode = (mode: number, t: TFunction) => {
  switch (mode) {
    case AudioEncoderMode.STEREO:
      return t('audioEncoder.mode.stereo');
    case AudioEncoderMode.LEFT:
      return t('audioEncoder.mode.monoLeft');
    case AudioEncoderMode.RIGHT:
      return t('audioEncoder.mode.monoRight');
    case AudioEncoderMode.SURROUND_THREE:
      return t('audioEncoder.mode.surroundThree');
    case AudioEncoderMode.SURROUND_FOUR:
      return t('audioEncoder.mode.surroundFour');
    case AudioEncoderMode.SURROUND_FIVE:
      return t('audioEncoder.mode.surroundFive');
    case AudioEncoderMode.SURROUND_SIX:
      return t('audioEncoder.mode.surroundSix');
    case AudioEncoderMode.SURROUND_SEVEN:
      return t('audioEncoder.mode.surroundSeven');
    case AudioEncoderMode.SURROUND_EIGHT:
      return t('audioEncoder.mode.surroundEight');
  }
  return '';
};

const encoderLang = (lang: string) => {
  if (!isNilOrEmpty(lang)) {
    return !isNilOrEmpty(audioLanguages[lang]) ? audioLanguages[lang] : lang;
  } else {
    return undefined; // best undefined for sorting purpose
  }
};

export interface AudioEncoderResponse extends GenericResponse {
  info: AudioEncoder;
  stats: AudioEncoderStats;
}

export interface GetAllAudioEncodersResponse {
  data: AudioEncoderResponse[];
}

export interface AudioEncoderViewModel extends AudioEncoder {
  stats: AudioEncoderStats;
  state: AudioEncoderState;

  stateSortable: number;
  interfaceTxt: string;
  inputInterface?: string; //ex. SDI-1
  langTxt: string | null;
  modeTxt: string;

  status: string;
  selected: boolean /* checked by checkbox */;
  hidden: boolean /* when filtered out */;
  updated: string /* to trigger setState to detect change and re-render */;
}

export const mapAudioEncoderViewModel = (response: AudioEncoderResponse, t: TFunction): AudioEncoderViewModel => {
  const interfaceOptions = getAudioInterfaceLabels(response.info.interface, response.info.mode);
  return {
    ...response.info,
    stats: { ...response.stats },
    state: response.stats.state,
    stateSortable: encoderStateToSortable(response.stats.state),
    interfaceTxt: interfaceOptions?.interfaceStr,
    inputInterface: interfaceOptions?.inputInterface,
    langTxt: encoderLang(response.info.lang),
    modeTxt: encoderMode(response.info.mode, t),
    bitRate: response.info.bitRate?.toString(),

    status: encoderStateToStatus(response.stats.state), // required for ActionBar
    selected: false,
    hidden: false,
    updated: new Date().toISOString(),
  };
};

export const mapAudioEncodersResponse = (
  response: GetAllAudioEncodersResponse,
  t: TFunction,
): AudioEncoderViewModel[] => {
  const mapped = response?.data.map((item) => mapAudioEncoderViewModel(item, t));
  return mapped;
};

/*
 * Validation
 */
export const audioEncoderValidationSchema = (t: TFunction, bitRateRange: () => MkRange) =>
  yup.object().shape({
    algorithm: yup.number().required(), // Algorithm is required and of type number
    bitRate: yup.number().when('algorithm', {
      is: (algorithm: number) => algorithm !== AudioAlgorithmEnum.RAW, // Only validate when algorithm is not 1 : PassThrough
      then: yup.number().test(
        'within-range',
        () => t('validation.ValidBitRate', { min: bitRateRange().min, max: bitRateRange().max }),
        (value) => {
          if (value < bitRateRange().min || value > bitRateRange().max) {
            return false;
          }
          return true;
        },
      ),
      otherwise: yup.number().nullable(true), // No validation if algorithm equals 1 : PassThrough
    }),

    name: yup.string().max(63, t('validation.maxLength', { length: 63 })), // Validate string max length
    lang: yup.string().length(3, t('validation.audioEncoder.lang')), // Validate lang has exactly 3 characters
    /*lang: yup.string().test('is-3', t('validation.audioEncoder.lang'), (value) => {
      console.log('lang validate', value);
      if (value?.length !== 3) {
        return false;
      }
      return true;
    }),*/
  });
